import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import {
  advanceSchoolOnboarding,
  cleanupSchoolOnboarding,
  completeSchoolOnboarding,
  seedOnboardingAfterProduct,
  seedOnboardingFeeRequests,
  startSchoolOnboarding,
} from "../../lib/schools";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import { FieldHint, FORM_STYLE_CARD_CLASS, SectionTitle } from "../createEvent/CreateEventFormUi";

const STEPS = [
  { id: "venue", label: "Add a venue", tab: "venues" },
  { id: "class", label: "Add a class", tab: "classes" },
  { id: "product", label: "Create a product", tab: "products" },
  { id: "promote", label: "Promote a practice student to instructor", tab: "people" },
  { id: "fees", label: "Review practice fee requests", tab: "action-items" },
  { id: "cleanup", label: "Remove practice students", tab: null },
  { id: "invite", label: "Share your invite link", tab: null },
];

function continueLabel(step) {
  if (step === "cleanup") return "Remove practice students";
  if (step === "invite") return "Copy invite & finish";
  if (step === "product") return "Add practice students";
  if (step === "promote") return "I’ve promoted an instructor";
  return "Continue";
}

export default function SchoolOnboardingPanel({
  school,
  venues = [],
  classes = [],
  products = [],
  onSchoolUpdated,
  onSwitchTab,
  onCopyInvite,
  onPeopleRefresh,
}) {
  const [busy, setBusy] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const step = school?.onboarding_step;
  const completed = Boolean(school?.onboarding_completed_at);

  if (!school || completed) return null;
  // Only offer / show for empty schools or active onboarding
  if (!step && classes.length > 0) return null;

  function goToAction(tab) {
    if (tab) onSwitchTab?.(tab);
  }

  async function run(fn) {
    setBusy(true);
    try {
      const next = await fn();
      if (next?.school) onSchoolUpdated?.(next.school);
      else if (next) onSchoolUpdated?.(next);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!step) {
    return (
      <ReadableCard className={`${FORM_STYLE_CARD_CLASS} border-orchid/30`} data-testid="school-onboarding-cta">
        <SectionTitle>Practice setup</SectionTitle>
        <FieldHint>
          Walk through one venue, class, and product with temporary practice students — then share your real invite.
        </FieldHint>
        <div className="flex flex-wrap gap-2 mt-3">
          <ReadableNavButton
            type="button"
            variant="primary"
            disabled={busy}
            onClick={() => run(async () => {
              const schoolDoc = await startSchoolOnboarding(school.id);
              goToAction("venues");
              return schoolDoc;
            })}
            data-testid="onboarding-start"
          >
            Start practice setup
          </ReadableNavButton>
          <ReadableNavButton
            type="button"
            disabled={busy}
            onClick={() => run(() => completeSchoolOnboarding(school.id))}
            data-testid="onboarding-skip"
          >
            Skip for now
          </ReadableNavButton>
        </div>
      </ReadableCard>
    );
  }

  const currentIdx = STEPS.findIndex((s) => s.id === step);
  const currentStep = STEPS[currentIdx] || STEPS[0];
  const stepNumber = Math.max(currentIdx, 0) + 1;

  async function continueStep() {
    if (step === "venue") {
      if (!venues.length) {
        toast.error("Add a venue first (Venues tab).");
        goToAction("venues");
        return;
      }
      await run(async () => {
        const s = await advanceSchoolOnboarding(school.id);
        goToAction("classes");
        return s;
      });
      return;
    }
    if (step === "class") {
      if (!classes.length) {
        toast.error("Add a class first (Classes tab).");
        goToAction("classes");
        return;
      }
      await run(async () => {
        const s = await advanceSchoolOnboarding(school.id);
        goToAction("products");
        return s;
      });
      return;
    }
    if (step === "product") {
      if (!products.length) {
        toast.error("Create a product first (Products tab).");
        goToAction("products");
        return;
      }
      await run(async () => {
        const result = await seedOnboardingAfterProduct(school.id);
        toast.success("Practice students added — promote one to instructor on People.");
        await onPeopleRefresh?.();
        goToAction("people");
        return result.school;
      });
      return;
    }
    if (step === "promote") {
      setBusy(true);
      try {
        const result = await seedOnboardingFeeRequests(school.id);
        toast.success("Two practice payments are waiting on Action Items.");
        await onPeopleRefresh?.();
        onSchoolUpdated?.(result.school);
        goToAction("action-items");
      } catch (e) {
        toast.error(formatApiError(e));
        goToAction("people");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === "fees") {
      await run(async () => {
        const s = await advanceSchoolOnboarding(school.id);
        return s;
      });
      return;
    }
    if (step === "cleanup") {
      await run(async () => {
        const s = await cleanupSchoolOnboarding(school.id);
        toast.success("Practice students removed.");
        await onPeopleRefresh?.();
        return s;
      });
      return;
    }
    if (step === "invite") {
      onCopyInvite?.();
      await run(() => completeSchoolOnboarding(school.id));
      toast.success("You’re set — share that invite with real students.");
    }
  }

  return (
    <ReadableCard
      className={`${FORM_STYLE_CARD_CLASS} border-orchid/30 !p-4`}
      data-testid="school-onboarding-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-ui text-[12px] font-semibold text-ink/55">
            Practice setup · Step {stepNumber} of {STEPS.length}
          </p>
          <p className="font-ui font-semibold text-[15px] text-ink mt-0.5 text-pretty">
            {currentStep.label}
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 inline-flex items-center gap-1 min-h-[44px] px-2 rounded-md2 font-ui text-[13px] font-semibold text-plum hover:bg-orchid/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
          aria-expanded={stepsOpen}
          onClick={() => setStepsOpen((v) => !v)}
          data-testid="onboarding-toggle-steps"
        >
          {stepsOpen ? "Hide steps" : "All steps"}
          {stepsOpen ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
        </button>
      </div>

      {stepsOpen ? (
        <ol className="mt-3 space-y-2 border-t border-black/[0.06] pt-3">
          {STEPS.map((s, idx) => {
            const done = currentIdx > idx;
            const current = s.id === step;
            return (
              <li
                key={s.id}
                className={`flex items-start gap-2 text-sm ${current ? "font-semibold text-ink" : done ? "text-ink/50" : "text-ink/40"}`}
                data-testid={`onboarding-step-${s.id}`}
              >
                <span className="mt-0.5 shrink-0 w-5 text-center" aria-hidden>
                  {done ? "✓" : current ? "→" : "○"}
                </span>
                <button
                  type="button"
                  className="text-left hover:underline disabled:no-underline"
                  disabled={!s.tab}
                  onClick={() => s.tab && goToAction(s.tab)}
                >
                  {s.label}
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <span className="sr-only" data-testid={`onboarding-step-${currentStep.id}`}>
          Current step: {currentStep.label}
        </span>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <ReadableNavButton
          type="button"
          variant="primary"
          disabled={busy}
          onClick={continueStep}
          data-testid="onboarding-continue"
        >
          {continueLabel(step)}
        </ReadableNavButton>
        <ReadableNavButton
          type="button"
          disabled={busy}
          onClick={() => run(() => completeSchoolOnboarding(school.id))}
          data-testid="onboarding-skip-active"
        >
          Skip for now
        </ReadableNavButton>
      </div>
    </ReadableCard>
  );
}
